import { useState } from 'react';
import { Copy, Check, Link2, Trash2, RotateCw } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { copyToClipboard } from '@/lib/utils/clipboard';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/lib/hooks/use-toast';

interface ShareClientDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description?: string;
  /** Token actual del recurso (null si no se compartió aún) */
  currentToken: string | null;
  /** Fecha de expiración actual (ISO) o null si no expira */
  currentExpiresAt: string | null;
  /** Genera o regenera el token y devuelve { token, expiresAt } */
  onGenerate: (
    expiresInDays: number | undefined
  ) => Promise<{ token: string; expiresAt: string | null }>;
  /** Revoca el token */
  onRevoke: () => Promise<void>;
  /** Construye la URL pública a partir del token */
  buildUrl: (token: string) => string;
  /**
   * Lo que ve el cliente en el link, según el recurso. Sin la prop, el de un pedido de importación (con tramos).
   * Una cotización no tiene tramos: la del Pricer pasa la suya (recorrido DEV 17-oct).
   */
  queVeElCliente?: string[];
}

const LO_QUE_VE_DE_UN_PEDIDO = [
  'Estado y fechas de avance',
  'Monto total a pagar',
  'Productos / ítems',
  'Tramos (origen, destino y fechas)',
];

const EXPIRY_OPTIONS: { value: string; label: string; days: number | undefined }[] = [
  { value: '30', label: '30 días', days: 30 },
  { value: '90', label: '90 días', days: 90 },
  { value: '365', label: '1 año', days: 365 },
  { value: 'never', label: 'Sin expiración', days: undefined },
];

export function ShareClientDialog({
  open,
  onOpenChange,
  title,
  description,
  currentToken,
  currentExpiresAt,
  onGenerate,
  onRevoke,
  buildUrl,
  queVeElCliente = LO_QUE_VE_DE_UN_PEDIDO,
}: ShareClientDialogProps) {
  const { toast } = useToast();
  const [token, setToken] = useState<string | null>(currentToken);
  const [expiresAt, setExpiresAt] = useState<string | null>(currentExpiresAt);
  const [expiryChoice, setExpiryChoice] = useState<string>('90');
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  const url = token ? buildUrl(token) : null;

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const days = EXPIRY_OPTIONS.find((o) => o.value === expiryChoice)?.days;
      const result = await onGenerate(days);
      setToken(result.token);
      setExpiresAt(result.expiresAt);
      toast({ title: 'Link generado', description: 'Listo para compartir con el cliente.' });
    } catch (err: any) {
      toast({
        title: 'No pudimos generar el link',
        description: err?.message ?? 'Intentá de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRevoke = async () => {
    setLoading(true);
    try {
      await onRevoke();
      setToken(null);
      setExpiresAt(null);
      toast({ title: 'Link revocado' });
    } catch (err: any) {
      toast({
        title: 'No pudimos revocar',
        description: err?.message ?? 'Intentá de nuevo.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (!url) return;
    const ok = await copyToClipboard(url);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } else {
      toast({ title: 'No pudimos copiar', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent aria-describedby={undefined} className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Link2 className="w-5 h-5 text-primary" />
            {title}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-2">
          {description && <p className="text-sm text-muted-foreground">{description}</p>}

          <div className="rounded-md border bg-muted/30 p-3 text-xs text-muted-foreground">
            <p className="font-semibold text-foreground mb-1">El cliente verá:</p>
            <ul className="list-disc list-inside space-y-0.5">
              {queVeElCliente.map((q) => (
                <li key={q}>{q}</li>
              ))}
            </ul>
            <p className="mt-2 text-[11px]">
              No se exponen proveedores, costos internos, márgenes ni notas.
            </p>
          </div>

          {!token ? (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Caducidad del link</Label>
                <Select value={expiryChoice} onValueChange={setExpiryChoice}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={handleGenerate} disabled={loading} className="w-full">
                {loading ? 'Generando…' : 'Generar link público'}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-xs">Link para el cliente</Label>
                <div className="flex gap-2 mt-1">
                  <Input value={url ?? ''} readOnly className="font-mono text-xs" />
                  <Button type="button" variant="outline" onClick={handleCopy} className="shrink-0">
                    {copied ? (
                      <Check className="w-4 h-4 text-emerald-600" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </Button>
                </div>
                <p className="text-[11px] text-muted-foreground mt-1">
                  {expiresAt
                    ? `Expira el ${new Date(expiresAt).toLocaleDateString('es-BO', { day: '2-digit', month: 'short', year: 'numeric' })}`
                    : 'Sin expiración'}
                </p>
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleGenerate}
                  disabled={loading}
                  className="flex-1"
                >
                  <RotateCw className="w-3.5 h-3.5 mr-1.5" />
                  Renovar caducidad
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleRevoke}
                  disabled={loading}
                  className="text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Revocar
                </Button>
              </div>

              <div>
                <Label className="text-xs">Caducidad al renovar</Label>
                <Select value={expiryChoice} onValueChange={setExpiryChoice}>
                  <SelectTrigger className="h-9 mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {EXPIRY_OPTIONS.map((o) => (
                      <SelectItem key={o.value} value={o.value}>
                        {o.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
